import { Skeleton } from '@mui/material';
import styled from 'styled-components';
import { StyledPaper, TitleRow } from '../../Styled/Styled';
import { CustomerDetailsContainer, FieldGrid, FieldLabel, FieldValue } from './Styled';

/**
 * Value widths the card placeholders cycle through, so a card of six rows does not read as six
 * identical bars. Percentages rather than px: the same rows have to look right in the ~640px panel and
 * in the page's 65/35 split.
 */
const VALUE_WIDTHS = ['62%', '45%', '78%', '52%', '70%', '38%'];

/** Label widths, cycled the same way. The real labels are one to three words. */
const LABEL_WIDTHS = ['5.5rem', '7rem', '6.25rem'];

/**
 * Stands in for the order title in `OrderDetailsHeader` — an `h2` holding the delivery type and the
 * order number. Rendered inside `PanelTopBar` next to the close button, whose place at the right edge
 * this preserves: it is `flex: 1` for the same reason the real title is.
 */
export const OrderTitleSkeleton: React.FC = () => <TitleSkeleton variant="text" width="55%" height={32} aria-hidden />;

/**
 * The row under the title, standing in for `OrderStatusChip` and, where the order has one, the preorder
 * badge. One pill only: whether there is a second one is something only the order can say.
 */
export const OrderMetaSkeleton: React.FC = () => (
  <MetaRow aria-hidden>
    <PillSkeleton variant="rectangular" width={128} height={32} />
  </MetaRow>
);

/**
 * A card in the shape of the label/value cards the details view is built from: the real heading, which
 * is a fixed string and not something being waited on, over `rows` placeholder pairs in the same
 * `FieldGrid` the real cards use. Same paper, same padding, same grid — so the values land where their
 * placeholders were instead of shifting the column.
 */
export const OrderCardSkeleton: React.FC<{ title?: string; rows?: number }> = ({ title, rows = 4 }) => (
  <StyledPaper variant="outlined">
    <CustomerDetailsContainer>
      {title && (
        <TitleRow>
          <h3>{title}</h3>
        </TitleRow>
      )}
      <FieldGrid aria-hidden>
        {Array.from({ length: rows }, (_, index) => (
          <Row key={index}>
            <FieldLabel>
              <Skeleton variant="text" width={LABEL_WIDTHS[index % LABEL_WIDTHS.length]} />
            </FieldLabel>
            <FieldValue>
              <Skeleton variant="text" width={VALUE_WIDTHS[index % VALUE_WIDTHS.length]} />
            </FieldValue>
          </Row>
        ))}
      </FieldGrid>
    </CustomerDetailsContainer>
  </StyledPaper>
);

/**
 * The products card while the product, category, ingredient and size caches are filling. Rows in the
 * shape of `OrderProducts`' `ProductRow` — size and quantity, then the name and the price pushed apart
 * — built from the same measurements, including the divider each product row carries on top.
 *
 * A fixed three rows: how many products the order has is in the order, which by this point has already
 * arrived, but reading it here would buy a placeholder nobody is looking at that long.
 */
export const OrderProductsSkeleton: React.FC<{ title: string }> = ({ title }) => (
  <StyledPaper variant="outlined">
    <CustomerDetailsContainer>
      <TitleRow>
        <h3>{title}</h3>
      </TitleRow>
      <div aria-hidden>
        {Array.from({ length: 3 }, (_, index) => (
          <ProductLine key={index}>
            <Skeleton variant="text" width="3.5rem" />
            <Skeleton variant="text" width="1.5rem" />
            <NameAndPrice>
              <Skeleton variant="text" width={VALUE_WIDTHS[index % VALUE_WIDTHS.length]} />
              <Skeleton variant="text" width={52} />
            </NameAndPrice>
          </ProductLine>
        ))}
      </div>
    </CustomerDetailsContainer>
  </StyledPaper>
);

/**
 * The actions card: its heading and one button-shaped placeholder. Sized to the small outlined button
 * `OrderActions` renders, so the card is the height it will be rather than a text line short of it.
 */
export const OrderActionsSkeleton: React.FC<{ title: string }> = ({ title }) => (
  <StyledPaper variant="outlined">
    <TitleRow>
      <h3>{title}</h3>
    </TitleRow>
    <ButtonSkeleton variant="rectangular" width="15rem" height={31} aria-hidden />
  </StyledPaper>
);

// `flex: 1` and the h2's line height, so the row it shares with the close button is exactly as tall as
// it will be once the title itself is there.
const TitleSkeleton = styled(Skeleton)`
  flex: 1;
  margin: 0;
`;

// The same row the header's status and preorder badge sit on.
const MetaRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

// The status chip's radius, the same override `OrdersListSkeleton` makes for the list's status pill.
const PillSkeleton = styled(Skeleton)`
  border-radius: 999px;
`;

// MUI's small button radius, so the placeholder reads as a button rather than as a bar.
const ButtonSkeleton = styled(Skeleton)`
  border-radius: 4px;
`;

/**
 * One label/value pair. `display: contents` so the two cells stay direct grid items of `FieldGrid` and
 * keep the real cards' column alignment, while the pair is still a single element to key on.
 */
const Row = styled.div`
  display: contents;
`;

// `ProductRow`'s measurements: the divider above each product, its 16px of air, and the gap between the
// size, the quantity and the name.
const ProductLine = styled.div`
  display: flex;
  flex-direction: row;
  align-items: baseline;
  gap: 8px;
  border-top: solid lightgray 1px;
  padding: 16px 0 8px;
`;

// The name on the left, the price against the right edge — `PriceDetails` in the real row.
const NameAndPrice = styled.div`
  display: flex;
  flex: 1;
  min-width: 0;
  justify-content: space-between;
  gap: 8px;
`;
